import { item } from "./data/items.ts";
import { CHAPTERS, PROLOGUE, STORY_FIGHTS } from "./data/story.ts";
import { saveHero } from "./save.ts";
import { SPECIAL_INFO } from "./rules.ts";
import { cutsceneScreen } from "./screens/cutscene.ts";
import { endingScreen } from "./screens/ending.ts";
import { hubScreen } from "./screens/hub.ts";
import type { Backdrop, Hero, Opponent } from "./types.ts";

let current: Hero | null = null;

export function hero(): Hero {
  if (!current) throw new Error("no hero loaded");
  return current;
}

export function setHero(h: Hero): void {
  current = h;
  saveHero(h);
}

export function persist(): void {
  if (current) saveHero(current);
}

export function chapterIndex(progress: number): number {
  return Math.min(CHAPTERS.length - 1, Math.floor(progress / 4));
}

export function backdropFor(h: Hero): Backdrop {
  return h.finished ? "colosseum" : (CHAPTERS[chapterIndex(h.progress)]?.backdrop ?? "colosseum");
}

export function nextStoryFight(h: Hero): { chapter: number; opponent: Opponent } | null {
  return h.finished ? null : (STORY_FIGHTS[h.progress] ?? null);
}

/** World domination percentage: one twentieth per story fight won. */
export function domination(h: Hero): number {
  return Math.round((Math.min(h.progress, STORY_FIGHTS.length) / STORY_FIGHTS.length) * 100);
}

export function startStory(): void {
  cutsceneScreen({ lines: PROLOGUE, backdrop: "barn", onDone: toHub });
}

/** Go to the Roost, playing the current chapter's intro first if it hasn't been seen. */
export function toHub(): void {
  const h = hero();
  if (!h.finished) {
    const ch = chapterIndex(h.progress);
    const key = `intro-${ch}`;
    const chapter = CHAPTERS[ch];
    if (chapter && !h.seenScenes.includes(key)) {
      h.seenScenes.push(key);
      persist();
      cutsceneScreen({ lines: chapter.intro, backdrop: chapter.backdrop, chapter: ch, onDone: toHub });
      return;
    }
  }
  hubScreen();
}

/**
 * Records a story win. Returns reward lines to show on the victory screen and
 * what to do when the player continues.
 */
export function winStoryFight(opponent: Opponent): { lines: string[]; next: () => void } {
  const h = hero();
  const ch = chapterIndex(h.progress);
  h.progress++;
  const chapter = CHAPTERS[ch];
  if (!opponent.boss || !chapter) {
    persist();
    return { lines: [], next: toHub };
  }
  const lines = [`${chapter.region} conquered! +${chapter.reward.gold} gold`];
  h.gold += chapter.reward.gold;
  const special = chapter.reward.special;
  if (special && !h.specials.includes(special)) {
    h.specials.push(special);
    lines.push(`Learned Fowl Art: ${SPECIAL_INFO[special].name}`);
  }
  const loot = chapter.reward.item;
  if (loot && !h.inventory.includes(loot)) {
    h.inventory.push(loot);
    lines.push(`Looted: ${item(loot).name}`);
  }
  const final = ch === CHAPTERS.length - 1;
  if (final) h.finished = true;
  persist();
  return {
    lines,
    next: () => cutsceneScreen({ lines: chapter.outro, backdrop: chapter.backdrop, onDone: final ? endingScreen : toHub }),
  };
}
